import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useChartOfAccounts, useUpdateChartOfAccount, ChartOfAccount } from "@/hooks/useChartOfAccounts";
import { toast } from "sonner";
import { Loader2, Check, X, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";

const typeBadge = (type: string) => {
  const colors: Record<string, string> = {
    income: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cogs: "bg-red-50 text-red-700 border-red-200",
    expense: "bg-amber-50 text-amber-700 border-amber-200",
    liability: "bg-purple-50 text-purple-700 border-purple-200",
    asset: "bg-blue-50 text-blue-700 border-blue-200",
    equity: "bg-gray-50 text-gray-700 border-gray-200",
  };
  return colors[type] || "";
};

export const ChartOfAccountsTab = () => {
  const { data: accounts = [], isLoading } = useChartOfAccounts();
  const updateAccount = useUpdateChartOfAccount();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [qbName, setQbName] = useState("");
  const [qbId, setQbId] = useState("");

  // Build tree: parent accounts first, then children
  const parentAccounts = accounts.filter((a) => !a.parent_account);
  const childMap = new Map<string, ChartOfAccount[]>();
  accounts.forEach((a) => {
    if (a.parent_account) {
      const children = childMap.get(a.parent_account) || [];
      children.push(a);
      childMap.set(a.parent_account, children);
    }
  });

  const startEdit = (a: ChartOfAccount) => {
    setEditingId(a.id);
    setQbName(a.qb_account_name || "");
    setQbId(a.qb_account_id || "");
  };

  const saveEdit = async (id: string) => {
    try {
      await updateAccount.mutateAsync({
        id,
        qb_account_name: qbName || null,
        qb_account_id: qbId || null,
      });
      toast.success("QB mapping saved");
      setEditingId(null);
    } catch (err: any) {
      toast.error(err.message || "Failed to save");
    }
  };

  const renderRow = (a: ChartOfAccount, indent: boolean = false) => (
    <TableRow key={a.id}>
      <TableCell className={cn("font-mono text-sm", indent && "pl-10")}>
        {a.account_number}
      </TableCell>
      <TableCell className={cn("font-medium", indent && "text-muted-foreground text-sm")}>
        {indent && "└ "}{a.name}
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={typeBadge(a.account_type)}>
          {a.account_type}
        </Badge>
      </TableCell>
      <TableCell>
        {editingId === a.id ? (
          <div className="flex items-center gap-1">
            <Input
              value={qbName}
              onChange={(e) => setQbName(e.target.value)}
              placeholder="QB Account Name"
              className="h-7 text-xs w-40"
            />
            <Input
              value={qbId}
              onChange={(e) => setQbId(e.target.value)}
              placeholder="QB ID"
              className="h-7 text-xs w-20"
            />
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => saveEdit(a.id)} disabled={updateAccount.isPending}>
              <Check className="h-3 w-3 text-primary" />
            </Button>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditingId(null)}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <span className="text-sm text-muted-foreground">
              {a.qb_account_name || "—"}
            </span>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => startEdit(a)}>
              <Pencil className="h-3 w-3" />
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Chart of Accounts</h2>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Account #</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-28">Type</TableHead>
                <TableHead>QB Mapping</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></TableCell></TableRow>
              ) : parentAccounts.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No accounts configured</TableCell></TableRow>
              ) : parentAccounts.map((parent) => (
                <>
                  {renderRow(parent)}
                  {(childMap.get(parent.account_number) || []).map((child) => renderRow(child, true))}
                </>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
